import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from '../../../shared/components/toast/useToast';
import { instagramApi } from '../api/instagramApi';
import type {
  InstagramConnectionStatusView,
  InstagramConnectionSummary,
  InstagramPlatformCredential,
  UpdateInstagramSettingsPayload,
  UpsertInstagramPlatformCredentialPayload,
} from '../types';

const KEY = 'instagram';

/** Nest validation errors arrive as `message: string[]`; take the first line. */
const errMsg = (err: unknown, fallback: string) => {
  const msg = (err as { message?: unknown })?.message;
  if (Array.isArray(msg)) return (msg as string[])[0] || fallback;
  return (typeof msg === 'string' ? msg : '') || fallback;
};

const toastUnlessAlready = (
  toastError: (message: string) => void,
  err: unknown,
  fallback: string,
) => {
  if (!(err as { toasted?: boolean })?.toasted) toastError(errMsg(err, fallback));
};

// ─── Merchant self-service ──────────────────────────────────────────────────

export const useMyInstagramStatus = (enabled = true) =>
  useQuery<InstagramConnectionStatusView>({
    queryKey: [KEY, 'me', 'status'],
    queryFn: instagramApi.getMyStatus,
    enabled,
  });

/**
 * Starts the OAuth handshake. The success path is a FULL PAGE NAVIGATION to Instagram, so
 * there is nothing to invalidate — Meta redirects back to osprey-api's callback, which in
 * turn redirects the browser to `/settings/instagram?instagram=connected|error`.
 */
export const useConnectInstagram = () => {
  const { toast } = useToast();
  return useMutation({
    mutationFn: instagramApi.connect,
    onSuccess: ({ authorizeUrl }) => {
      window.location.href = authorizeUrl;
    },
    onError: (err: unknown) =>
      toastUnlessAlready(
        toast.error,
        err,
        'Could not start the Instagram connection. The platform app credential may not be configured yet.',
      ),
  });
};

export const useDisconnectInstagram = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: instagramApi.disconnect,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [KEY] });
      toast.success('Instagram disconnected');
    },
    onError: (err: unknown) => toastUnlessAlready(toast.error, err, 'Failed to disconnect Instagram'),
  });
};

export const useUpdateInstagramSettings = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (payload: UpdateInstagramSettingsPayload) => instagramApi.updateMySettings(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [KEY] });
      toast.success('Instagram settings saved');
    },
    onError: (err: unknown) => toastUnlessAlready(toast.error, err, 'Failed to save Instagram settings'),
  });
};

// ─── TCS admin oversight ────────────────────────────────────────────────────

export const useInstagramConnections = (enabled = true) =>
  useQuery<InstagramConnectionSummary[]>({
    queryKey: [KEY, 'connections'],
    queryFn: instagramApi.listConnections,
    enabled,
  });

export const useResetInstagramConnection = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (merchantId: string) => instagramApi.resetConnection(merchantId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [KEY] });
      toast.success('Connection reset — the merchant can reconnect cleanly');
    },
    onError: (err: unknown) => toastUnlessAlready(toast.error, err, 'Failed to reset the connection'),
  });
};

// ─── TCS admin platform credential ──────────────────────────────────────────

/**
 * ⚠️ Expected to 403 for every role today — MERCHANT.MANAGE is granted to nobody (see
 * instagramApi.ts). `retry: false` keeps a known-permanent 403 from being retried three
 * times before the page can render its permission-denied state.
 */
export const useInstagramPlatformCredential = (enabled = true) =>
  useQuery<InstagramPlatformCredential | null>({
    queryKey: [KEY, 'platform-credential'],
    queryFn: instagramApi.getPlatformCredential,
    enabled,
    retry: false,
  });

export const useUpsertInstagramPlatformCredential = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (payload: UpsertInstagramPlatformCredentialPayload) =>
      instagramApi.upsertPlatformCredential(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [KEY, 'platform-credential'] });
      toast.success('Instagram app credential saved');
    },
    onError: (err: unknown) => toastUnlessAlready(toast.error, err, 'Failed to save the app credential'),
  });
};
